import React from "react";

import { Close } from "@mui/icons-material";
import {
  Alert,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import { format } from "date-fns";

import { Type } from "@features/ui";
import { OrderVariant } from "@models/OrderVariant";
import { TrackingResponse } from "@models/TrackingResponse";
import { TrackingResponseError } from "@models/TrackingResponse";

import { useOrderVariantTrackingQuery } from "../queries";

const isTrackingResponseError = (
  data: TrackingResponse | TrackingResponseError
): data is TrackingResponseError => {
  return "errors" in data;
};

const TrackingContent = ({ orderVariantId }: { orderVariantId: string }) => {
  const { data } = useOrderVariantTrackingQuery(orderVariantId);

  if (!data) {
    return <CircularProgress />;
  }

  if (isTrackingResponseError(data)) {
    return (
      <>
        <Alert severity="warning" className="max-w-prose text-lg">
          This order was shipped by a freight carrier. To get tracking updates,
          please Google the freight carrier name and track your shipment through
          their website.
        </Alert>
        <pre className="mt-4 text-xs text-neutral-400">
          {data.errors[0].message}
        </pre>
      </>
    );
  }

  return (
    <div className="grid gap-6 grid-cols-2 items-start">
      <Card className="sticky top-0 p-6">
        <Type.H2>
          Tracking Status:{" "}
          <b className="font-medium">{data.carrierStatusDescription}</b>
        </Type.H2>
        {data.trackingUrl && (
          <Type.P className="mb-4">
            <a
              href={data.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Track on carrier website
            </a>
          </Type.P>
        )}
        <Type.P>Tracking Number: {data.trackingNumber}</Type.P>
        <Type.P>Carrier: {data.carrierCode}</Type.P>

        <Type.P>Status: {data.carrierStatusDescription}</Type.P>
        {data.actualDeliveryDate ? (
          <Type.P>
            Delivered on {format(new Date(data.actualDeliveryDate), "PPPP p")}
          </Type.P>
        ) : data.estimatedDeliveryDate ? (
          <Type.P>
            Estimated Delivery:{" "}
            {format(new Date(data.estimatedDeliveryDate), "PPPP")}
          </Type.P>
        ) : (
          <Type.P>Has not left original location yet.</Type.P>
        )}
      </Card>
      <div>
        <div className="divide-y">
          {data.events.map((event, index) => (
            <div key={index} className="py-3">
              <div className="flex gap-2 flex-wrap items-baseline justify-between">
                <Type.Small className="uppercase text-xs text-primary-500 mb-1">
                  {event.status_description}
                </Type.Small>
                <Type.Small>
                  {format(new Date(event.occurred_at), "PP p")}
                </Type.Small>
              </div>
              <Type.P className="text-lg">{event.description}</Type.P>
              {event.city_locality && (
                <Type.P className="text-neutral-400 text-sm">
                  {event.city_locality}, {event.state_province}{" "}
                  {event.postal_code}
                </Type.P>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const OrderVariantTrackingModal = ({
  onClose,
  orderVariant,
}: {
  onClose: () => void;
  orderVariant: OrderVariant;
}) => {
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className="flex items-center justify-between">
        Tracking for #{orderVariant.variant.item.itemNumber} on order #
        {orderVariant.order.id}
        <IconButton edge="end" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className="max-h-[480px]">
        <TrackingContent orderVariantId={orderVariant.id} />
      </DialogContent>
    </Dialog>
  );
};

export default OrderVariantTrackingModal;
